/**
 * Catégories d’événements & seuils pour les mécanismes d’allocation Entrea.
 */

export const ASSISTANCE_EVENT_CATEGORY_KEYS = [
  'deces',
  'hospitalisation',
  'maladie',
  'naissance',
  'mariage',
  'catastrophe',
  'autre',
];

const ACCENT_ALIASES = {
  deces: 'deces',
  décès: 'deces',
  hospitalisation: 'hospitalisation',
  maladie: 'maladie',
  naissance: 'naissance',
  mariage: 'mariage',
  catastrophe: 'catastrophe',
  autre: 'autre',
};

/** Normalise un code catégorie (accents, séparateurs, casse). */
export function normalizeAssistanceCategoryCode(raw) {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!s) return '';
  return ACCENT_ALIASES[s] || s;
}

/**
 * Éclate listes legacy mal formées :
 * - ["décès; mariage; naissance"] → ["deces","mariage","naissance"]
 * - "deces, mariage" → idem
 */
export function expandAssistanceCategories(raw) {
  const chunks = [];
  const pushParts = (val) => {
    String(val || '')
      .split(/[,;|/]+/)
      .forEach((part) => {
        const code = normalizeAssistanceCategoryCode(part);
        if (code) chunks.push(code);
      });
  };

  if (Array.isArray(raw)) {
    raw.forEach((item) => pushParts(item));
  } else if (raw != null) {
    pushParts(raw);
  }

  const seen = new Set();
  const out = [];
  for (const c of chunks) {
    if (seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

export function normalizeCategoryThresholds(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const category = normalizeAssistanceCategoryCode(row.category);
    if (!category || seen.has(category)) continue;
    seen.add(category);
    const min_amount = Math.max(0, Number(row.min_amount) || 0);
    const max_amount = Math.max(0, Number(row.max_amount) || 0);
    out.push({ category, min_amount, max_amount });
  }
  return out;
}

/** Lignes UI : une par catégorie connue + éventuelles catégories custom du mécanisme. */
export function buildMechanismCategoryRows(mechanism) {
  const covered = expandAssistanceCategories(mechanism?.covered_event_categories);
  const thresholds = normalizeCategoryThresholds(mechanism?.category_thresholds);
  const byCat = Object.fromEntries(thresholds.map((r) => [r.category, r]));
  const keys = [...ASSISTANCE_EVENT_CATEGORY_KEYS];
  for (const c of covered) {
    if (!keys.includes(c)) keys.push(c);
  }
  return keys.map((category) => {
    const thr = byCat[category];
    return {
      category,
      enabled: covered.includes(category),
      min_amount: thr ? String(thr.min_amount || '') : '',
      max_amount: thr ? String(thr.max_amount || '') : '',
    };
  });
}

export function defaultMechanismCategoryRows(enabledKeys = ['deces', 'mariage', 'naissance']) {
  const enabled = new Set(enabledKeys.map(normalizeAssistanceCategoryCode));
  return ASSISTANCE_EVENT_CATEGORY_KEYS.map((category) => ({
    category,
    enabled: enabled.has(category),
    min_amount: '',
    max_amount: '',
  }));
}

/** Payload API depuis les lignes UI. */
export function mechanismCategoriesPayloadFromRows(rows) {
  const safe = Array.isArray(rows) ? rows : [];
  const covered = [];
  const thresholds = [];
  for (const row of safe) {
    if (!row?.enabled) continue;
    const category = normalizeAssistanceCategoryCode(row.category);
    if (!category) continue;
    covered.push(category);
    const min_amount = Math.max(0, Number(row.min_amount) || 0);
    const max_amount = Math.max(0, Number(row.max_amount) || 0);
    if (min_amount > 0 || max_amount > 0) {
      thresholds.push({
        category,
        min_amount,
        max_amount: max_amount > 0 ? max_amount : min_amount,
      });
    }
  }
  const mins = thresholds.map((t) => t.min_amount).filter((n) => n > 0);
  const maxs = thresholds.map((t) => t.max_amount).filter((n) => n > 0);
  return {
    covered_event_categories: covered,
    category_thresholds: thresholds,
    assistance_thresholds: {
      min_amount: mins.length ? Math.min(...mins) : 0,
      max_amount: maxs.length ? Math.max(...maxs) : 0,
    },
  };
}

/** Montant suggéré pour une catégorie (max → min → 0). */
export function suggestedAmountForCategory(mechanism, category, fallback = 0) {
  const code = normalizeAssistanceCategoryCode(category);
  const rows = normalizeCategoryThresholds(mechanism?.category_thresholds);
  const hit = rows.find((r) => r.category === code);
  if (hit) {
    if (hit.max_amount > 0) return hit.max_amount;
    if (hit.min_amount > 0) return hit.min_amount;
  }
  const globalMax = Number(mechanism?.assistance_thresholds?.max_amount) || 0;
  const globalMin = Number(mechanism?.assistance_thresholds?.min_amount) || 0;
  if (globalMax > 0) return globalMax;
  if (globalMin > 0) return globalMin;
  return Number(fallback) || 0;
}

export function coveredCategoriesForMechanism(mechanism) {
  return expandAssistanceCategories(mechanism?.covered_event_categories);
}

export function eventCategoryLabelKey(code) {
  const c = normalizeAssistanceCategoryCode(code);
  return `memberDashboard.eventCategory.${c}`;
}

/** Formulaire règlement d’éligibilité (mécanisme). */
export function defaultEligibilityForm() {
  return {
    text: '',
    minimum_seniority_months: '', // vide = hériter des paramètres projet
    require_active_status: true,
    contribution_situation_mode: 'inherit', // inherit | auto | off
  };
}

export function eligibilityFormFromMechanism(mechanism) {
  const raw = mechanism?.eligibility_rules;
  if (typeof raw === 'string') {
    return {
      ...defaultEligibilityForm(),
      text: raw,
    };
  }
  const rules = raw && typeof raw === 'object' ? raw : {};
  const seniority =
    rules.minimum_seniority_months == null || rules.minimum_seniority_months === ''
      ? ''
      : String(rules.minimum_seniority_months);
  const mode = String(rules.contribution_situation_mode || 'inherit').toLowerCase();
  return {
    text: String(rules.text || ''),
    minimum_seniority_months: seniority,
    require_active_status: rules.require_active_status !== false,
    contribution_situation_mode: ['inherit', 'auto', 'off'].includes(mode) ? mode : 'inherit',
  };
}

export function eligibilityPayloadFromForm(form) {
  const f = form || defaultEligibilityForm();
  const seniorityRaw = String(f.minimum_seniority_months ?? '').trim();
  return {
    text: String(f.text || '').trim(),
    minimum_seniority_months: seniorityRaw === '' ? null : Math.max(0, Number(seniorityRaw) || 0),
    require_active_status: f.require_active_status !== false,
    contribution_situation_mode: String(f.contribution_situation_mode || 'inherit'),
  };
}

export function formatEligibilitySummary(mechanism, t) {
  const form = eligibilityFormFromMechanism(mechanism);
  const parts = [];
  if (form.require_active_status) {
    parts.push(t('assistanceManagement.eligibilityActiveShort', { defaultValue: 'Membre actif' }));
  }
  if (form.minimum_seniority_months !== '') {
    parts.push(
      t('assistanceManagement.eligibilitySeniorityShort', {
        count: form.minimum_seniority_months,
        defaultValue: 'Ancienneté ≥ {{count}} mois',
      })
    );
  } else {
    parts.push(
      t('assistanceManagement.eligibilitySeniorityInherit', {
        defaultValue: 'Ancienneté (paramètres)',
      })
    );
  }
  if (form.contribution_situation_mode === 'auto') {
    parts.push(
      t('assistanceManagement.eligibilityContribAuto', { defaultValue: 'Cotisations à jour' })
    );
  } else if (form.contribution_situation_mode === 'off') {
    parts.push(
      t('assistanceManagement.eligibilityContribOff', { defaultValue: 'Cotisations non contrôlées' })
    );
  } else {
    parts.push(
      t('assistanceManagement.eligibilityContribInherit', {
        defaultValue: 'Cotisations (paramètres)',
      })
    );
  }
  return parts.join(' · ');
}

